import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  UserIcon,
  IdentificationIcon,
  MegaphoneIcon,
  HeartIcon,
  BanknotesIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../../store/authStore';
import { SidebarSection, SidebarMenuItem } from '../../../types';

const SIDEBAR_SECTIONS: SidebarSection[] = [
  {
    titleKey: 'myProfile.sidebar.accountManagement',
    items: [
      { labelKey: 'myProfile.sidebar.myInformation', path: 'info', icon: UserIcon },
      { labelKey: 'myProfile.sidebar.profile', path: 'profile', icon: IdentificationIcon },
    ],
  },
  {
    titleKey: 'myProfile.sidebar.activityManagement',
    items: [
      { labelKey: 'myProfile.sidebar.myCampaign', path: 'campaigns', icon: MegaphoneIcon },
      // { labelKey: 'myProfile.sidebar.interestCampaign', path: 'interests', icon: HeartIcon },
      { labelKey: 'myProfile.sidebar.settlement', path: 'settlement', icon: BanknotesIcon },
    ],
  },
  {
    titleKey: 'myProfile.sidebar.customerService',
    items: [
      { labelKey: 'myProfile.sidebar.notice', path: '/notice', icon: BellIcon, absolute: true },
      { labelKey: 'myProfile.sidebar.inquiry', path: '/inquiry', icon: ChatBubbleLeftRightIcon, absolute: true },
    ],
  },
];

export interface ProfileSidebarProps {
  sections?: SidebarSection[];
  activePath?: string;
  onLogout?: () => void;
  variant?: 'standalone' | 'embedded';
  onItemClick?: () => void;
}

export const ProfileSidebar: React.FC<ProfileSidebarProps> = ({
  sections = SIDEBAR_SECTIONS,
  activePath: activePathProp,
  onLogout: onLogoutProp,
  variant = 'standalone',
  onItemClick,
}) => {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuthStore();

  // Derive active path from current route if not provided via props
  const currentSubPath = location.pathname.replace('/my-profile/', '').replace('/my-profile', '');
  const activePath = activePathProp ?? currentSubPath;

  const isActive = (itemPath: string, absolute?: boolean) => {
    if (absolute) return location.pathname === itemPath;
    return activePath === itemPath;
  };

  const handleNavigate = (path: string, absolute?: boolean) => {
    navigate(absolute ? path : `/my-profile/${path}`);
    onItemClick?.();
  };

  const handleLogout = async () => {
    if (!window.confirm(t('myProfile.sidebar.logoutConfirm', '로그아웃 하시겠습니까?'))) return;
    if (onLogoutProp) {
      onLogoutProp();
    } else {
      await logout();
      navigate('/login');
    }
  };

  // Flatten all items for mobile tab bar
  const allItems: (SidebarMenuItem & { isLogout?: boolean })[] = sections.flatMap(
    (section) => section.items
  );

  // Embedded variant: just render nav items (used inside Layout's sidebar)
  if (variant === 'embedded') {
    return (
      <>
        {sections.map((section, sectionIdx) => (
          <div key={section.titleKey} className={sectionIdx > 0 ? 'mt-6' : ''}>
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {t(section.titleKey)}
            </h3>
            <ul className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path, item.absolute);
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => handleNavigate(item.path, item.absolute)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        active
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Icon className={`h-5 w-5 mr-3 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                      {t(item.labelKey)}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        <div className="mt-1">
          <ul>
            <li>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 flex-shrink-0 text-gray-400" />
                {t('myProfile.sidebar.logout')}
              </button>
            </li>
          </ul>
        </div>
      </>
    );
  }

  // Standalone variant: full sidebar with wrapper
  return (
    <>
      {/* Desktop sidebar (≥1024px) */}
      <aside className="hidden lg:block w-64 min-h-full bg-white border-r border-gray-200 py-6 px-4">
        {sections.map((section, sectionIdx) => (
          <div key={section.titleKey} className={sectionIdx > 0 ? 'mt-6' : ''}>
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {t(section.titleKey)}
            </h3>
            <ul className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path, item.absolute);
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => handleNavigate(item.path, item.absolute)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        active
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Icon className={`h-5 w-5 mr-3 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                      {t(item.labelKey)}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* Logout button at the bottom of Customer Service section */}
        <div className="mt-1">
          <ul>
            <li>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 flex-shrink-0 text-gray-400" />
                {t('myProfile.sidebar.logout')}
              </button>
            </li>
          </ul>
        </div>
      </aside>

      {/* Mobile horizontal scrollable tab bar (<1024px) */}
      <nav className="lg:hidden bg-white border-b border-gray-200 overflow-x-auto">
        <div className="flex items-center px-2 py-2 space-x-1 min-w-max">
          {allItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.absolute);
            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path, item.absolute)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Icon className={`h-4 w-4 mr-1.5 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                {t(item.labelKey)}
              </button>
            );
          })}
          {/* Logout tab on mobile */}
          <button
            onClick={handleLogout}
            className="flex items-center px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1.5 flex-shrink-0 text-gray-400" />
            {t('myProfile.sidebar.logout')}
          </button>
        </div>
      </nav>
    </>
  );
};
